import List from "@ui-kit/data-display/List/List.web";

import { useTranslation } from "@shared/utils/internationalization";
import Text from "@ui-kit/typography/Text/Text.web";
import ListItem from "@ui-kit/data-display/ListItem/ListItem.web";
import Icon from "@ui-kit/icons/Icon.web";
import type { ComboBoxInputItemType } from "@ui-kit/form/ComboBox/ComboBox.props";
import type { MultiSelectedListItemsSharedProps } from "./MultiSelectedListItems.props";

export type MultiSelectedListItemsProps<
  T extends ComboBoxInputItemType = ComboBoxInputItemType
> = MultiSelectedListItemsSharedProps<T>;

export default function MultiSelectedListItems<T extends ComboBoxInputItemType>(
  props: MultiSelectedListItemsProps<T>
): JSX.Element {
  const {
    selectedItems,
    match = undefined,
    getSelectedItemProps,
    removeSelectedItem,
  } = props;
  const { t } = useTranslation("common");
  return (
    <List
      gap="sm"
      padding="md"
      tabIndex={-1}
      css={{
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
      }}
    >
      {selectedItems.map((selectedItem, index) => (
        <ListItem
          key={selectedItem.id}
          minHeight="element.2xl"
          padding="md"
          maxWidth="100%"
          justifyContent="space-between"
          alignItems="center"
          cursor="pointer"
          borderRadius="sm"
          borderWidth="1px"
          borderColor="gray.300"
          background="gray.300"
          textAlign="left"
          {...getSelectedItemProps({ selectedItem, index })}
          aria-label={t(`Button to remove ${selectedItem.name}`)}
          onClick={(event) => {
            event?.stopPropagation();
            event?.preventDefault();
            removeSelectedItem(selectedItem);
          }}
          as="button"
          tabIndex={0}
          height={match ? "auto" : "32px"}
          css={{
            display: "inline-flex",
            minWidth: 0,
          }}
        >
          {match ? (
            match(selectedItem)
          ) : (
            <Text
              fontSize="sm"
              isTruncated
              color="fg"
              flex="1"
              minWidth={0}
              title={selectedItem.name}
            >
              {selectedItem.name}
            </Text>
          )}
          <Icon.X.Outline
            alignSelf={match ? "flex-start" : "center"}
            h="element.lg"
            w="element.lg"
            ml="xs"
            mr="-4px"
          />
        </ListItem>
      ))}
    </List>
  );
}
